// src/client/lib/api.ts
/// <reference types="vite/client" />

import { getInitDataRaw, getCurrentTgId } from "./auth";

/* =================================================== */

const BASE = "/api";

/* ================= DEBUG ================= */
// Включить логи: window.__API_DEBUG = true  или VITE_API_DEBUG=1
const DEBUG =
  (typeof window !== "undefined" && (window as any).__API_DEBUG === true) ||
  String((import.meta as any)?.env?.VITE_API_DEBUG || "") === "1" ||
  import.meta.env.MODE !== "production";

function safeJson(obj: unknown, max = 800): string {
  try {
    const s = JSON.stringify(obj);
    return s.length > max ? s.slice(0, max) + "…(" + s.length + "b)" : s;
  } catch {
    return "[unserializable]";
  }
}
function logStart(tag: string, url: string, payload?: any) {
  if (!DEBUG) return () => {};

  // Suppress noisy endpoints unless explicitly enabled
  try {
    const noisy = (typeof window !== 'undefined') && (window as any).__API_NOISY === true;
    const path = String(url).split('?')[0];
    const isNoisyEndpoint = /\/onboarding\/get$/.test(path) || /\/me$/.test(path);
    if (isNoisyEndpoint && !noisy) return () => {};
  } catch {}

  const t0 = performance.now?.() ?? Date.now();
  const authLen = (getInitDataRaw() || "").length;
  console.groupCollapsed(`%cAPI %c${tag}%c ${url}`, "color:#888", "color:#0aa", "color:#888");
  if (payload) {
    const raw = safeJson(payload);
    let parsed: any = raw;
    try { parsed = JSON.parse(raw); } catch { /* keep string */ }
    console.log("→ payload:", parsed);
  } else {
    console.log("→ payload:", null);
  }
  console.log("→ _auth.len:", authLen);
  return (ok: boolean, extra?: any) => {
    const dt = Math.round((performance.now?.() ?? Date.now()) - t0);
    if (ok) {
      console.log("✓ done in", dt + "ms");
      if (extra !== undefined) console.log("← data:", extra);
    } else {
      console.log("✗ failed in", dt + "ms");
      if (extra !== undefined) console.log("← error:", extra);
    }
    console.groupEnd();
  };
}
/* ======================================== */

/* ---------- common types ---------- */
export type MeResp = {
  ok: boolean;
  me?: { id: number; username?: string; name?: string } | null;
  role?: string | null;
};

export type StatsResp = {
  entries_today: number;
  denied_today: number;
  waiting_now: number;
};

/* ---------- events (server uses snake_case) ---------- */
export type EventSuboption = {
  id?: number;
  title: string;
  price_rub?: number | null;
  description?: string | null;
  position?: number | null;

  disabled?: boolean;
  available_at?: string | null;
};

export type EventOption = {
  id?: number;
  title: string;
  price_rub?: number | null;
  description?: string | null;
  position?: number | null;
  starts_at?: string | null;
  ends_at?: string | null;

  disabled?: boolean;
  available_at?: string | null;

  suboptions?: EventSuboption[];
};

export type ActiveEventResponse = {
  ok: true;
  event: {
    id: number;
    title: string;
    currency?: string | null;
    pay_note?: string | null;
    payment_link?: string | null;
    payee_name?: string | null;
    payment_methods?: {
      phone?: string | null;
      card?: string | null;
      sbp_url?: string | null;
      phone_label?: string | null;
      card_label?: string | null;
      sbp_label?: string | null;
    } | null;
    options: EventOption[];
  } | null;
};

export type EventRow = {
  id: number;
  title: string;
  slug: string | null;
  venue: string | null;
  city: string | null;
  starts_at: string | null;
  ends_at: string | null;
  status: "draft" | "active" | "archived";
  currency: string | null;
  qr_valid_for_minutes: number | null;
  payee_name: string | null;
  payment_link: string | null;
  pay_note: string | null;

  pay_phone?: string | null;
  pay_card?: string | null;
  pay_sbp_url?: string | null;
  pay_phone_label?: string | null;
  pay_card_label?: string | null;
  pay_sbp_label?: string | null;

  pay_code_prefix: string | null;

  eula_required?: boolean;
  eula_text?: string | null;

  options?: EventOption[];

  created_at?: string;
  updated_at?: string;
  created_by?: number | null;
};

// Пэйлоады
export type CreateEventPayload = {
  title: string;
  venue?: string | null;
  city?: string | null;
  starts_at: string;
  ends_at: string;
  status: "draft" | "active" | "archived";
  currency?: string | null;
  qr_valid_for_minutes?: number | null;
  payee_name?: string | null;

  payment_link?: string | null;
  pay_phone?: string | null;
  pay_card?: string | null;
  pay_sbp_url?: string | null;
  pay_phone_label?: string | null;
  pay_card_label?: string | null;
  pay_sbp_label?: string | null;

  pay_note?: string | null;
  pay_code_prefix?: string | null;
  eula_required?: boolean | null;
  eula_text?: string | null;

  options: EventOption[];
};

export type UpdateEventPayload = Partial<CreateEventPayload> & {
  options?: EventOption[];
};

/* ---------- staff ---------- */
export type StaffRole = "owner" | "superadmin" | "admin" | "door";
export type StaffRoleGrant = Exclude<StaffRole, "owner">;

export type StaffRow = {
  tg_id: number;
  username?: string | null;
  name?: string | null;
  role: StaffRole;
  active: boolean;
  event_id?: number | null;
};

/* ---------- support buttons ---------- */
export type SupportButton = {
  id: number;
  label: string;
  action: "pay" | "ceo" | "main" | "ubass_form" | "link" | "text";
  payload?: string | null;
  enabled: boolean;
  sort_order: number;
  created_at?: string;
  updated_at?: string;
};

/* ---------- messages ---------- */
export type GuestStage = "eula" | "profile" | "payment" | "pending" | "pass" | "error" | "done";

export type SendMessagePayload =
  | {
      audience: "staff";
      roles: StaffRole[];
      text: string;
      disable_preview?: boolean;
      button_text?: string | null;
      button_url?: string | null;
      test_tg_id?: number | null;
    }
  | {
      audience: "guests";
      stages: GuestStage[];
      event_id?: number | null;
      text: string;
      disable_preview?: boolean;
      button_text?: string | null;
      button_url?: string | null;
      test_tg_id?: number | null;
    };

export type SendMessageResp = {
  ok: boolean;
  total?: number;
  sent?: number;
  failed?: number;
  test?: boolean;
};

export type HistoryItem = {
  id: number;
  created_at: string;
  audience: "staff" | "guests";
  criteria: string;
  text: string;
  sent: number;
  failed: number;
  test: boolean;
};

/* ---------- EULA / Onboarding ---------- */
export type EulaStatus = {
  required: boolean;
  accepted_at?: string | null;
  event_id?: number | null;
  text?: string | null;
};

export type OnboardingGetResp = { ok: boolean; event_id: number | null; step: Step | null };
// фронтовый enum; сервер дополнительно понимает "kinds"/"profile" и "student_wait"
export type Step = "eula" | "payment" | "pending" | "pass" | "done";

export type IssueEntryResp = {
  ok: true;
  token: string;
  url: string;
  expires_at: string;
};

/* ---------- helpers ---------- */
function withTimeout<T>(p: Promise<T>, ms = 10000): Promise<T> {
  return new Promise((resolve, reject) => {
    const t = setTimeout(() => reject(new Error("timeout")), ms);
    p.then(
      (v) => { clearTimeout(t); resolve(v); },
      (e) => { clearTimeout(t); reject(e); }
    );
  });
}

// --- no-auth pub/sub + one-shot bootstrap retry
const __noAuthSubs = new Set<() => void>();
let __bootstrapInflight: Promise<void> | null = null;
let __bootstrapTried = false;

export function onNoAuth(cb: () => void) {
  __noAuthSubs.add(cb);
  return () => __noAuthSubs.delete(cb);
}

async function runBootstrapOnce() {
  if (__bootstrapInflight) return __bootstrapInflight;
  if (__bootstrapTried) return Promise.resolve();
  __bootstrapTried = true;
  __bootstrapInflight = (async () => {
    try {
      const dynTgId = getCurrentTgId();
      let startUrl = `${BASE}/miniapp/start`;
      try {
        if (dynTgId) {
          const u = new URL(startUrl, globalThis.location?.origin || 'http://localhost');
          u.searchParams.set('tg_id', String(dynTgId));
          startUrl = u.toString();
        }
      } catch {}

      await withTimeout(
        fetch(startUrl, {
          method: "POST",
          headers: { "Content-Type": "application/json" },
          credentials: "include",
          body: JSON.stringify({}),
        }),
        8000
      ).catch(() => null);
    } finally {
      __bootstrapInflight = null;
    }
  })();
  return __bootstrapInflight;
}

function signalNoAuth() {
  try { __noAuthSubs.forEach(fn => { try { fn(); } catch {} }); } catch {}
}

// POST JSON with cookies & optional init-data headers
async function http<T>(path: string, body?: unknown): Promise<T> {
  const _auth =
    getInitDataRaw() ||
    (typeof window !== "undefined" && (window as any)?.Telegram?.WebApp?.initData) ||
    null;

  let url = `${BASE}${path}`;
  // If there is no initData header available, append ?tg_id=... as a safe dev/Android fallback
  const dynTgId = getCurrentTgId();
  if (!_auth && dynTgId) {
    try {
      const u = new URL(url, globalThis.location?.origin || 'http://localhost');
      u.searchParams.set('tg_id', String(dynTgId));
      url = u.toString();
    } catch {}
  }
  const payload = { ...(body || {}), _auth };

  const headers: Record<string, string> = { "Content-Type": "application/json" };
  if (_auth) {
    headers["X-Telegram-Init-Data"] = String(_auth);
    headers["X-Telegram-Web-App-Init-Data"] = String(_auth);
  }

  const end = logStart("POST", url, payload);
  let res = await withTimeout(
    fetch(url, {
      method: "POST",
      headers,
      credentials: "include",
      body: JSON.stringify(payload),
    }),
    10000
  ).catch((e) => { end(false, e?.message || e); throw e; });

  if (res.status === 401 && !_auth) {
    signalNoAuth();
    await runBootstrapOnce().catch(() => {});
    res = await withTimeout(
      fetch(url, {
        method: "POST",
        headers,
        credentials: "include",
        body: JSON.stringify(payload),
      }),
      10000
    ).catch(() => res);
  }

  if (!res.ok) {
    const text = await res.text().catch(() => "");
    end(false, { status: res.status, text: text.slice(0, 200) });
    throw new Error(`HTTP ${res.status} ${res.statusText} | ${text.slice(0, 200)}`);
  }

  const data = await res.json().catch(() => ({} as any));
  end(true, data);
  return data;
}

// (наследие) YK
export async function createPayment(input: {
  userId: number;
  eventId: number;
  optionId: number;
  receipt?: any;
}) {
  const r = await fetch('/api/payments/create', {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify(input),
  });
  if (!r.ok) {
    const err = await r.json().catch(() => ({}));
    throw new Error(err?.error || 'PAYMENT_CREATE_FAILED');
  }
  return r.json() as Promise<{
    confirmationUrl: string;
    status: string;
    ykPaymentId: string;
  }>;
}

/* ---------- cache for /me ---------- */
type MeCache = { ts: number; data: MeResp | null; inflight: Promise<MeResp> | null };
const __meCache: MeCache = { ts: 0, data: null, inflight: null };
const ME_TTL_MS = 15000;

async function fetchMe(): Promise<MeResp> {
  if (__meCache.inflight) return __meCache.inflight;
  const p = http<MeResp>("/me").then((res) => {
    __meCache.ts = Date.now();
    __meCache.data = res;
    __meCache.inflight = null;
    return res;
  }).catch((e) => {
    __meCache.inflight = null;
    throw e;
  });
  __meCache.inflight = p;
  return p;
}

async function getMeWithCache(): Promise<MeResp> {
  const now = Date.now();
  if (__meCache.data && (now - __meCache.ts) < ME_TTL_MS) return __meCache.data;
  return fetchMe();
}

/* ================= public API ================= */
export const api = {
  passes: {
    list: () => http<{ ok: boolean; passes: any[] }>("/passes/list"),
  },

  payments: {
    tinkoffCreate: (payload: {
      optionId?: number | null;
      amount?: number | null;
      promo_code?: string | null;
      orderId?: string | null;
      description?: string | null;
      successUrl?: string | null;
      failUrl?: string | null;
    }) => http<any>("/payments/tinkoff/create", payload),
  },

  health: () => http<{ ok: boolean; ts: number }>("/health"),
  me: () => getMeWithCache(),

  // door
  verify: (token: string) => http("/entry/verify", { token }),
  checkin: (token: string) => http<{ ok: boolean }>("/entry/checkin", { token }),
  deny: (token: string, note?: string) => http<{ ok: boolean }>("/entry/deny", { token, note }),
  stats: () => http<StatsResp>("/door/stats"),

  // events
  events: {
    list: () => http<EventRow[]>("/events/list"),
    get: (id: number) => http<EventRow>("/events/get", { id }),
    create: (data: CreateEventPayload) =>
      http<{ ok: boolean; id: number }>("/events/create", data),
    update: (
      id: number,
      patch: Partial<
        Omit<EventRow, "id" | "created_at" | "updated_at" | "created_by">
      > & {
        pay_phone?: string | null;
        pay_card?: string | null;
        pay_sbp_url?: string | null;
        pay_phone_label?: string | null;
        pay_card_label?: string | null;
        pay_sbp_label?: string | null;
        options?: EventOption[];
      }
    ) => http<{ ok: boolean }>("/events/update", { id, patch }),
  },

  // single active event (legacy helper)
  event: {
    active: () => http<ActiveEventResponse>("/event/active"),
  },

  // staff
  staff: {
    list: async (): Promise<StaffRow[]> => {
      const resp = await http<StaffRow[] | { ok?: boolean; staff?: StaffRow[] }>(
        "/staff/list"
      );
      return Array.isArray(resp) ? resp : (resp.staff || []);
    },
    add: (identifier: string, role: StaffRoleGrant) =>
      http<{ ok: boolean; created?: boolean; updated?: boolean }>(
        "/staff/add",
        { identifier, role }
      ),
    setActive: (tg_id: number, active: boolean) =>
      http<{ ok: boolean }>("/staff/setActive", { tg_id, active }),
    setRole: (tg_id: number, role: StaffRoleGrant) =>
      http<{ ok: boolean }>("/staff/setRole", { tg_id, role }),
    remove: (tg_id: number) => http<{ ok: boolean }>("/staff/remove", { tg_id }),
  },

  // messages
  messages: {
    send: (p: SendMessagePayload) => http<SendMessageResp>("/messages/send", p),
    history: () => http<HistoryItem[]>("/messages/history"),
  },

  // admin promo codes
  adminPromo: {
    list: (p?: {
      limit?: number;
      offset?: number;
      search?: string;
      eventId?: number | null;
      active?: boolean | null;
    }) => http<{ ok: boolean; items: any[] }>("/admin/promo/list", p || {}),
    create: (p: Record<string, any>) => http<{ ok: boolean; id?: number }>("/admin/promo/create", p),
    update: (p: Record<string, any>) => http<{ ok: boolean; updated?: number }>("/admin/promo/update", p),
    toggle: (id: number, active: boolean) => http<{ ok: boolean }>("/admin/promo/toggle", { id, active }),
    remove: (id: number) => http<{ ok: boolean }>("/admin/promo/delete", { id }),
  },

  // admin settings (owner only)
  adminSettings: {
    get: (key?: string) => http<{ ok: boolean; item?: { key: string; value: string } | null; items?: { key: string; value: string }[] }>(
      "/admin/settings/get",
      key ? { key } : {}
    ),
    set: (key: string, value: string) => http<{ ok: boolean }>("/admin/settings/set", { key, value }),
  },

  // admin support buttons
  adminSupportButtons: {
    list: () => http<{ ok: boolean; items: SupportButton[] }>("/admin/support/list", {}),
    create: (p: Partial<SupportButton>) =>
      http<{ ok: boolean; id?: number }>("/admin/support/create", p),
    update: (p: Partial<SupportButton> & { id: number }) =>
      http<{ ok: boolean; updated?: number }>("/admin/support/update", p),
    remove: (id: number) =>
      http<{ ok: boolean; deleted?: number }>("/admin/support/delete", { id }),
  },

  // eula/onboarding (guest)
  eula: {
    status: () => http<EulaStatus>("/eula/status"),
    accept: () => http<{ ok: boolean; accepted_at?: string }>("/eula/accept"),
  },

  onboarding: {
    // eventId опциональный: если не передадим — сервер возьмёт активное событие
    get: (eventId?: number | null) =>
      http<OnboardingGetResp>("/onboarding/get", eventId ? { event_id: eventId } : {}),
    set: (step: any, eventId?: number | null) =>
      http<OnboardingGetResp>("/onboarding/set", { step, event_id: eventId ?? undefined }),
    start: () => http<{ ok: boolean }>("/miniapp/start"),
  },

  entry: {
    issue: (eventId?: number | null) =>
      http<IssueEntryResp>(
        "/entry/issue",
        eventId ? { event_id: eventId } : {}
      ),
  },

  tinkoff: {
    create: (data: {
      optionId?: number;
      promo_code?: string | null;
      amount?: number | null;
      orderId?: string | null;
      description?: string | null;
      successUrl?: string | null;
      failUrl?: string | null;
    }) =>
      http<{
        ok: boolean;
        paymentId?: string | number | null;
        paymentUrl?: string | null;
        amount_rub?: number | null;
        discount_rub?: number | null;
        promo_code?: string | null;
        reason?: string;
        error?: string;
        url?: string;
      }>("/payments/tinkoff/create", data),
  },
};

export default api;
